import { isTauri } from '@tauri-apps/api/core';

// Mock missing tauri-plugins since they are not installed
const relaunch = async () => { window.location.reload(); };
const check = async (): Promise<any> => null;

export type UpdateState =
  | 'idle'
  | 'checking'
  | 'available'
  | 'up-to-date'
  | 'downloading'
  | 'ready'
  | 'error';

export interface UpdateStatus {
  state: UpdateState;
  availableVersion?: string;
  downloadedBytes?: number;
  totalBytes?: number;
  error?: string;
}

type Listener = (status: UpdateStatus) => void;

class UpdateService {
  private status: UpdateStatus = { state: 'idle' };
  private listeners = new Set<Listener>();
  private update: Awaited<ReturnType<typeof check>> | null = null;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.status);
    return () => this.listeners.delete(listener);
  }

  getStatus(): UpdateStatus {
    return this.status;
  }

  async checkForUpdates(options?: { background?: boolean }): Promise<void> {
    if (!isTauri()) return;
    if (this.status.state === 'checking' || this.status.state === 'downloading') return;

    const background = options?.background === true;
    this.emit({ state: 'checking', error: undefined, availableVersion: undefined });

    try {
      const update = await check();
      if (update?.available) {
        this.update = update;
        this.emit({ state: 'available', availableVersion: update.version });
      } else {
        this.update = null;
        this.emit({ state: 'up-to-date' });
      }
    } catch (error) {
      this.update = null;
      if (background) {
        this.emit({ state: 'idle', error: undefined });
        return;
      }
      this.emit({ state: 'error', error: this.humanizeError(error) });
    }
  }

  async downloadAndInstall(): Promise<void> {
    if (!isTauri() || !this.update) return;

    let downloaded = 0;
    this.emit({ state: 'downloading', downloadedBytes: 0, totalBytes: undefined, error: undefined });

    try {
      await this.update.downloadAndInstall((progress) => {
        if (progress.event === 'Progress') {
          downloaded += progress.data.chunkLength ?? 0;
          const totalBytes = 'contentLength' in progress.data && typeof progress.data.contentLength === 'number'
            ? progress.data.contentLength
            : undefined;
          this.emit({
            state: 'downloading',
            downloadedBytes: downloaded,
            totalBytes,
          });
        }
        if (progress.event === 'Finished') {
          this.emit({ state: 'ready' });
        }
      });

      if (this.status.state === 'downloading') {
        this.emit({ state: 'ready' });
      }
    } catch (error) {
      this.emit({ state: 'error', error: this.humanizeError(error) });
    }
  }

  async relaunch(): Promise<void> {
    if (!isTauri()) return;
    await relaunch();
  }

  private emit(patch: Partial<UpdateStatus>): void {
    this.status = { ...this.status, ...patch };
    this.listeners.forEach((listener) => listener(this.status));
  }

  private humanizeError(error: unknown): string {
    const message = String((error as { message?: string })?.message || error || '');
    const online = typeof navigator === 'undefined' ? true : navigator.onLine;

    if (!online) return 'Sin conexion. Revisa la red e intentalo de nuevo.';
    if (/signature|sig/i.test(message)) return 'La firma del update no pudo verificarse.';
    if (/404|500|502|503|504|timeout|release json|latest.json/i.test(message)) {
      return 'El canal de updates no esta respondiendo correctamente.';
    }
    if (/network|fetch|connect/i.test(message)) return 'No se pudo conectar con el canal de updates.';
    return message || 'Error interno al comprobar actualizaciones.';
  }
}

export const updateService = new UpdateService();
